import { useState } from "react";
import { MessageSquarePlus, Star, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/hooks/useAuth";
import { useOrg } from "@/hooks/useOrg";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { z } from "zod";

const feedbackSchema = z.object({
  category: z.enum(["bug", "feature", "ux", "general"]),
  message: z.string().trim().min(5, "Please write at least 5 characters").max(2000, "Max 2000 characters"),
  rating: z.number().min(1).max(5).optional(),
});

const CATEGORIES = [
  { value: "bug", label: "🐛 Bug Report" },
  { value: "feature", label: "💡 Feature Request" },
  { value: "ux", label: "🎨 UX / Design" },
  { value: "general", label: "💬 General" },
];

const FeedbackWidget = () => {
  const { user } = useAuth();
  const { org } = useOrg();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("general");
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  if (!user) return null;

  const handleSubmit = async () => {
    const parsed = feedbackSchema.safeParse({
      category,
      message,
      rating: rating > 0 ? rating : undefined,
    });

    if (!parsed.success) {
      toast({ title: "Validation error", description: parsed.error.issues[0].message, variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.from("feedback" as any).insert({
        user_id: user.id,
        org_id: org?.id || null,
        category,
        message: message.trim(),
        rating: rating > 0 ? rating : null,
        page_url: window.location.pathname,
      } as any);

      if (error) throw error;

      toast({ title: "Thanks for your feedback! 🎉", description: "We'll review it shortly." });
      setMessage("");
      setRating(0);
      setCategory("general");
      setOpen(false);
    } catch (e: any) {
      toast({ title: "Failed to submit", description: e.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 rounded-full bg-primary p-3 text-primary-foreground shadow-lg hover:bg-primary/90 transition-all"
        aria-label="Send feedback"
      >
        {open ? <X className="h-5 w-5" /> : <MessageSquarePlus className="h-5 w-5" />}
      </button>

      {/* Feedback panel */}
      {open && (
        <div className="fixed bottom-20 right-6 z-50 w-80 rounded-xl border border-border bg-card p-5 shadow-2xl animate-in slide-in-from-bottom-4 fade-in">
          <h3 className="text-sm font-semibold text-foreground mb-1">Send Feedback</h3>
          <p className="text-xs text-muted-foreground mb-4">Help us improve NetOps Security</p>

          {/* Category */}
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="mb-3 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Message */}
          <Textarea
            placeholder="What's on your mind?"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="mb-3 text-sm min-h-[80px] resize-none"
            maxLength={2000}
          />

          {/* Star rating */}
          <div className="flex items-center gap-1 mb-4">
            <span className="text-xs text-muted-foreground mr-2">Rating:</span>
            {[1, 2, 3, 4, 5].map((s) => (
              <button
                key={s}
                onClick={() => setRating(s === rating ? 0 : s)}
                onMouseEnter={() => setHoveredStar(s)}
                onMouseLeave={() => setHoveredStar(0)}
                className="p-0.5"
              >
                <Star
                  className={`h-4 w-4 transition-colors ${
                    s <= (hoveredStar || rating)
                      ? "fill-primary text-primary"
                      : "text-muted-foreground/40"
                  }`}
                />
              </button>
            ))}
          </div>

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            disabled={submitting || message.trim().length < 5}
            className="w-full gap-2"
            size="sm"
          >
            <Send className="h-3.5 w-3.5" />
            {submitting ? "Sending…" : "Submit Feedback"}
          </Button>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
